import React from 'react';

import classNames from 'classnames';

import { formatCommittingQty, formatMoneyWithLocale } from 'Helpers/formatService';

/**
 * RevenueImpactHeader
 *
 * The headline of the Commit Revenue screen — the revenue sibling of TicketingImpactHeader:
 *   Uncommitted now  →  This commitment  →  Uncommitted after
 *
 * Revenue is quantity-driven, so each stat carries a quantity alongside its value. The value
 * leads because that is what users reconcile on; the quantity sits beneath it as the figure the
 * budget actually stores.
 *
 * Over-commitment is permitted here rather than blocked (matching Direct Commitment and
 * diverging from ticketing), so a negative "after" balance is reachable and is rendered in red.
 *
 * Cash-only: a revenue line cannot carry a VIK commitment, so there is no VIK split.
 *
 * Props:
 *  - context:       { crumbs, line }         what is being committed against
 *  - now:           { quantity, value }      uncommitted balance before this commitment
 *  - committing:    { quantity, value }      what is being committed now
 *  - overCommitted: boolean                  committing more than the balance leaves
 */
export default function RevenueImpactHeader({ context = {}, now = { quantity: 0, value: 0 }, committing = { quantity: 0, value: 0 }, overCommitted = false }) {
    const after = {
        quantity: (now.quantity ?? 0) - (committing.quantity ?? 0),
        value: (now.value ?? 0) - (committing.value ?? 0),
    };

    const Stat = ({ label, quantity, value, negative = false, positive = false, highlight = false }) => {
        const isNegative = negative && value < 0;
        const isQuantityNegative = negative && quantity < 0;

        return (
            <div
                className={classNames('tw-px-5 tw-py-4 tw-min-w-0', {
                    'tw-bg-green-25/40': highlight && !isNegative,
                    'tw-bg-red-25/40': highlight && isNegative,
                    'tw-bg-charcoal-10': !highlight,
                })}
            >
                <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-charcoal-50 tw-font-semibold tw-mb-1">{label}</div>
                <div className="tw-flex tw-flex-col tw-gap-y-0.5">
                    <div
                        className={classNames('tw-text-xl tw-font-bold tw-tabular-nums', {
                            'tw-text-red': isNegative,
                            'tw-text-green': positive && !isNegative,
                            'tw-text-charcoal': !isNegative && !positive,
                        })}
                    >
                        {formatMoneyWithLocale(value)}
                    </div>
                    <div
                        className={classNames('tw-text-sm tw-font-semibold tw-tabular-nums', {
                            'tw-text-red': isQuantityNegative,
                            'tw-text-green': positive && !isQuantityNegative,
                            'tw-text-charcoal-75': !isQuantityNegative && !positive,
                        })}
                    >
                        {$t('Qty')}: {formatCommittingQty(quantity)}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div
            className={classNames(
                // 2x2 below xl (a narrow viewport plus the sidebar cannot fit four tiles in a
                // row), 1x4 from xl up. min-w-0 on every tile lets the grid shrink with the card.
                // Hairlines come from a 1px gap over the container background — divide-x/y only
                // draws column dividers, which breaks down in the 2x2 arrangement.
                'tw-grid tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-4 tw-gap-px tw-border tw-rounded-lg tw-overflow-hidden tw-bg-charcoal-15',
                overCommitted ? 'tw-border-red' : 'tw-border-charcoal-15',
            )}
        >
            <div className="tw-px-5 tw-py-4 tw-min-w-0 tw-bg-charcoal-10">
                {context.crumbs && <div className="tw-text-xs tw-text-charcoal-50 tw-font-medium tw-mb-1">{context.crumbs}</div>}
                <div className="tw-text-lg tw-font-bold tw-text-charcoal">{context.line || '-'}</div>
            </div>
            <Stat label={$t('Uncommitted now')} quantity={now.quantity ?? 0} value={now.value ?? 0} />
            {/* Positive and green: revenue is income, so the amount being committed is money
                arriving. It used to be rendered as a red negative, borrowed from the expenditure
                screen, which read as a loss. The balance it consumes is the "after" tile's job. */}
            <Stat label={$t('This commitment')} quantity={committing.quantity ?? 0} value={committing.value ?? 0} positive />
            <Stat label={$t('Uncommitted after')} quantity={after.quantity} value={after.value} negative highlight />
        </div>
    );
}
